import {
  Button,
  Card,
  Container,
  EmptyContent,
  Flex,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import { usePermissions } from '@/contexts/PermissionsContext';
import { Role } from '@/types/permissions';
import { shouldShowDebugFeatures } from '@/utils/debugUtils';
import {
  IconCheck,
  IconLockAccess,
  IconUserCircle,
  IconUserFilled,
} from '@tabler/icons-react';

interface AccessDeniedProps {
  requiredPermissions?: string[];
  message?: string;
  showRoleSwitcher?: boolean;
}

export function AccessDenied({
  requiredPermissions = [],
  message = "You don't have permission to access this page.",
  showRoleSwitcher = true,
}: AccessDeniedProps) {
  const { availableRoles, setActiveRole, activeRole } = usePermissions();

  const handleRoleChange = (role: Role) => {
    setActiveRole(role);
  };
  // TODO:  Slavik: Need to Improve layout

  return (
    <Flex
      direction="column"
      flexGrow="1"
      className="access-denied"
      style={{ height: '100%' }}
    >
      <EmptyContent
        withIconBox
        icon={<IconLockAccess />}
        title="Access Denied"
        description={message}
        state="error"
      >
        {showRoleSwitcher &&
          availableRoles.length > 1 &&
          shouldShowDebugFeatures() && (
            <Container
              size="large"
              style={{ marginTop: '2rem', textAlign: 'center', flexGrow: 0 }}
            >
              <Card size="large">
                <Text
                  style={{
                    textAlign: 'center',
                    marginBottom: '1rem',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  You have access to other roles. Try switching to a different
                  role:
                </Text>
                <Flex
                  mt="2"
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                  className="role-switcher"
                >
                  {availableRoles.map(role => {
                    const isCurrentRole = activeRole?.id === role.id;
                    return (
                      <Button
                        variant={!isCurrentRole ? 'surface' : 'surface'}
                        size="small"
                        key={role.id}
                        onClick={() => handleRoleChange(role)}
                        disabled={isCurrentRole}
                        startIcon={
                          isCurrentRole ? (
                            <IconUserFilled />
                          ) : (
                            <IconUserCircle />
                          )
                        }
                        endIcon={isCurrentRole && <IconCheck />}
                      >
                        {role.name}
                      </Button>
                    );
                  })}
                </Flex>
              </Card>
            </Container>
          )}
      </EmptyContent>

      <VisuallyHidden>
        {requiredPermissions.length > 0 && (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <details style={{ color: 'var(--color-text-secondary)' }}>
              <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
                Required Permissions
              </summary>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {requiredPermissions.map(permission => (
                  <li key={permission} style={{ marginBottom: '0.25rem' }}>
                    <code>{permission}</code>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}
      </VisuallyHidden>
    </Flex>
  );
}
